import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { listBranches, listCompanyOptions } from '../api/branchApi'

const DEFAULT_PAGE_SIZE = 10

export function useBranchListScreen() {
  const [viewMode, setViewMode] = useState('active')
  const [searchText, setSearchText] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
    }

    if (viewMode === 'archived') {
      params.archive = true
    } else if (viewMode === 'pending') {
      params.pending_review = true
      params.archive = false
    } else {
      params.archive = false
    }

    if (searchText.trim()) {
      params.search = searchText.trim()
    }

    if (companyId) {
      params.company_id = Number(companyId)
      params.companyId = Number(companyId)
    }

    if (fromDate) {
      params.from_date = fromDate
      params.fromdate = fromDate
    }

    if (toDate) {
      params.to_date = toDate
      params.enddate = toDate
    }

    return params
  }, [companyId, fromDate, page, pageSize, searchText, toDate, viewMode])

  useEffect(() => {
    let active = true

    async function loadCompanyOptions() {
      try {
        const options = await listCompanyOptions({ page: 1, page_size: 100 })
        if (!active) return
        setCompanyOptions(options)
      } catch {
        if (!active) return
        setCompanyOptions([])
      }
    }

    loadCompanyOptions()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true

    async function loadBranches() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listBranches(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          totalCount: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load branches.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadBranches()

    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  const totalPages = useMemo(() => {
    if (!meta.totalCount || !meta.pageSize) return meta.hasMore ? page + 1 : page
    return Math.max(1, Math.ceil(meta.totalCount / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.totalCount, page])

  const rows = useMemo(() => {
    return items.map((item) => {
      let createdDate = '-'
      if (item.created_at) {
        try {
          createdDate = new Date(item.created_at).toLocaleDateString('en-US', {
            month: 'short', day: '2-digit', year: 'numeric'
          })
        } catch { createdDate = item.created_at }
      }
      const isPending = Number(item.is_approved) === 0 || item.approval_status === 'PENDING'
      const isArchived = Number(item.is_archive) === 1
      const status = isArchived ? 'Archive / Deactive' : isPending ? 'Pending Review' : 'Active'
      return {
        id: item.id,
        branchCode: item.branch_code,
        name: item.name || '-',
        companyName: item.company_name || '-',
        regionLabel: item.city || item.state || 'Region',
        email: item.email || '-',
        phone: item.contact_number || '-',
        createdDate,
        isPending,
        isArchived,
        status,
      }
    })
  }, [items])

  const rangeLabel = useMemo(() => {
    return `Showing ${rows.length} of ${meta.totalCount || rows.length} branches`
  }, [meta.totalCount, rows.length])

  function setViewModeAndReset(nextMode) {
    setViewMode(nextMode)
    setPage(1)
  }

  function setSearchTextAndReset(value) {
    setSearchText(value)
    setPage(1)
  }

  function setCompanyIdAndReset(value) {
    setCompanyId(value)
    setPage(1)
  }

  function setFromDateAndReset(value) {
    setFromDate(value)
    setPage(1)
  }

  function setToDateAndReset(value) {
    setToDate(value)
    setPage(1)
  }

  function resetFilters() {
    setSearchText('')
    setCompanyId('')
    setFromDate('')
    setToDate('')
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    state: {
      viewMode,
      searchText,
      companyId,
      fromDate,
      toDate,
      page,
      totalPages,
      totalCount: meta.totalCount,
      hasMore: meta.hasMore,
      isLoading,
      error,
      rows,
      companyOptions,
      rangeLabel,
    },
    actions: {
      setViewMode: setViewModeAndReset,
      setSearchText: setSearchTextAndReset,
      setCompanyId: setCompanyIdAndReset,
      setFromDate: setFromDateAndReset,
      setToDate: setToDateAndReset,
      resetFilters,
      goToPage,
      goToPreviousPage,
      goToNextPage,
    },
  }
}
